import React, { useEffect, useRef } from 'react';
import { API_BASE_URL } from './config';

// MapLibre GL + OpenFreeMap: 100% free, no API key, no subscription.
// Vector tiles let us force English labels everywhere (OSM raster tiles render
// local-language names, e.g. Chinese). Geocoding goes through our own backend
// (backend/api/geocode.php -> Nominatim) so we stay within the OSM usage policy.
const MAPLIBRE_CSS = 'https://unpkg.com/maplibre-gl@4.7.1/dist/maplibre-gl.css';
const MAPLIBRE_JS = 'https://unpkg.com/maplibre-gl@4.7.1/dist/maplibre-gl.js';
const MAP_STYLE = 'https://tiles.openfreemap.org/styles/liberty';

// Load MapLibre (CSS + JS) once and resolve with window.maplibregl.
let maplibrePromise = null;
function loadMapLibre() {
  if (window.maplibregl) return Promise.resolve(window.maplibregl);
  if (maplibrePromise) return maplibrePromise;
  maplibrePromise = new Promise((resolve, reject) => {
    if (!document.querySelector(`link[href="${MAPLIBRE_CSS}"]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = MAPLIBRE_CSS;
      document.head.appendChild(link);
    }
    const script = document.createElement('script');
    script.src = MAPLIBRE_JS;
    script.async = true;
    script.onload = () => resolve(window.maplibregl);
    script.onerror = reject;
    document.body.appendChild(script);
  });
  return maplibrePromise;
}

/** Force every label layer to prefer English names. */
function forceEnglishLabels(map) {
  const style = map.getStyle();
  if (!style || !style.layers) return;
  const englishField = ['coalesce', ['get', 'name:en'], ['get', 'name:latin'], ['get', 'name']];
  style.layers.forEach((layer) => {
    if (layer.type === 'symbol' && layer.layout && 'text-field' in layer.layout) {
      try { map.setLayoutProperty(layer.id, 'text-field', englishField); } catch (e) { /* ignore */ }
    }
  });
}

/** Geocode a batch of "City, Country" strings via our free OSM-backed endpoint. */
async function geocodePlaces(places) {
  if (!places || places.length === 0) return [];
  try {
    const res = await fetch(`${API_BASE_URL}/geocode.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ places }),
    });
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data.results) ? data.results : [];
  } catch (e) {
    return [];
  }
}

/**
 * Reduce a free-text location to a city/place name (fallback path only, when a
 * trip has no per-day route cities). Takes the most city-like comma segment.
 */
function cityOf(loc) {
  let s = String(loc).trim();
  if (/ to /i.test(s)) s = s.split(/ to /i).pop().trim();
  const parts = s.split(',').map((x) => x.trim()).filter(Boolean);
  if (parts.length) {
    s = parts[parts.length - 1];
    for (let i = parts.length - 1; i >= 0; i--) {
      if (parts[i].length <= 30 && !/\b(and|including|near|between|via)\b/i.test(parts[i])) {
        s = parts[i];
        break;
      }
    }
  }
  s = s.replace(/\b(via|near|including|between|toward|towards)\b.*$/i, '').trim();
  s = s.replace(/\b(ancient city center|city cent(er|re)|old town|waterfront|promenade|south station|north station|border (crossing|terminal)|station|airport|mtr|area)\b/ig, '').trim();
  return s.replace(/[,\-–\s]+$/, '').trim();
}

/**
 * Ordered list of stops to plot. Each stop is { label, query }:
 *   label - what the marker/popup shows (English city name).
 *   query - what we geocode ("City, Country" is unambiguous).
 *
 * Primary source is the trip's per-day route cities (tripDays[].city/country) —
 * exactly the city shown on each day in the itinerary. We walk days in order
 * and collapse *consecutive* duplicates, so a city you stay in for several days
 * is one pin, but a city you return to later gets its own (revisits preserved).
 */
function buildStops({ tripDays, activitiesByDay, cities }) {
  const pushUnique = (seq, label, query) => {
    const clean = (label || '').trim();
    if (!clean || clean === '0') return;
    const prev = seq[seq.length - 1];
    if (prev && prev.label.toLowerCase() === clean.toLowerCase()) return; // collapse consecutive
    seq.push({ label: clean, query: (query || clean).trim() });
  };

  // 1) Preferred: per-day route cities.
  if (Array.isArray(tripDays) && tripDays.some((d) => d && (d.city || '').trim())) {
    const seq = [];
    tripDays
      .slice()
      .sort((a, b) => (a.dayNumber || 0) - (b.dayNumber || 0))
      .forEach((d) => {
        const city = (d.city || '').trim();
        if (!city) return; // gap day; consecutive-dedup spans it
        const country = (d.country || '').trim();
        pushUnique(seq, city, country ? `${city}, ${country}` : city);
      });
    return seq;
  }

  // 2) Fallback: explicit cities prop (legacy inline usage).
  if (Array.isArray(cities) && cities.length) {
    const seq = [];
    cities.forEach((c) => pushUnique(seq, cityOf(c), c));
    return seq;
  }

  // 3) Last resort: derive cities from activity locations.
  if (activitiesByDay) {
    const seq = [];
    Object.keys(activitiesByDay)
      .filter((k) => k !== 'unscheduled')
      .sort()
      .forEach((dayKey) => {
        (activitiesByDay[dayKey] || []).forEach((act) => {
          if (act.type === 'transportation') {
            const sd = act.structured_data || {};
            if (act.subtype === 'flight' && sd.details) {
              const dd = sd.details;
              if (dd.departure && dd.departure.iataCode) pushUnique(seq, dd.departure.iataCode);
              if (dd.arrival && dd.arrival.iataCode) pushUnique(seq, dd.arrival.iataCode);
            } else if (sd.transportation_details) {
              if (sd.transportation_details.to) pushUnique(seq, cityOf(sd.transportation_details.to));
            } else if (act.destination) {
              pushUnique(seq, cityOf(act.destination));
            }
          } else if (act.location) {
            pushUnique(seq, cityOf(act.location));
          }
        });
      });
    return seq;
  }

  return [];
}

/**
 * TripMap — two usage modes:
 *   Inline (ItineraryPage):  <TripMap cities={[...]} />
 *   Modal  (ItineraryPageV2): <TripMap open onClose tripDays activitiesByDay />
 */
const TripMap = ({ cities, open, onClose, tripDays, activitiesByDay }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);

  const isModalMode = open !== undefined;
  const stops = buildStops({ tripDays, activitiesByDay, cities });
  const stopsKey = stops.map((s) => s.query).join('|');

  useEffect(() => {
    if (isModalMode && !open) return;
    if (stops.length === 0 || !mapRef.current) return;

    let cancelled = false;

    loadMapLibre().then(async (maplibregl) => {
      if (cancelled || !mapRef.current) return;

      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
      const map = new maplibregl.Map({
        container: mapRef.current,
        style: MAP_STYLE,
        center: [0, 20],
        zoom: 1.5,
        attributionControl: true,
      });
      mapInstanceRef.current = map;
      map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');

      map.on('load', () => forceEnglishLabels(map));

      // Geocode the unique queries, then plot stops in order.
      const uniqueQueries = Array.from(new Set(stops.map((s) => s.query)));
      const located = await geocodePlaces(uniqueQueries);
      if (cancelled) return;
      const coordByQuery = {};
      located.forEach((r) => { coordByQuery[r.query.toLowerCase()] = [r.lng, r.lat]; });

      const plotted = stops
        .map((s, i) => ({ ...s, order: i + 1, coord: coordByQuery[s.query.toLowerCase()] }))
        .filter((s) => Array.isArray(s.coord));
      if (plotted.length === 0) return;

      const drawOverlay = () => {
        // Route line connecting the stops in order. A white casing underneath
        // keeps it visible on any basemap; arrowheads show travel direction.
        if (plotted.length > 1) {
          const line = {
            type: 'Feature',
            geometry: { type: 'LineString', coordinates: plotted.map((s) => s.coord) },
          };
          if (map.getSource('route')) {
            map.getSource('route').setData(line);
          } else {
            map.addSource('route', { type: 'geojson', data: line });
            map.addLayer({
              id: 'route-casing',
              type: 'line',
              source: 'route',
              layout: { 'line-cap': 'round', 'line-join': 'round' },
              paint: { 'line-color': '#ffffff', 'line-width': 7, 'line-opacity': 0.9 },
            });
            map.addLayer({
              id: 'route-line',
              type: 'line',
              source: 'route',
              layout: { 'line-cap': 'round', 'line-join': 'round' },
              paint: { 'line-color': '#7c5cff', 'line-width': 4 },
            });
            map.addLayer({
              id: 'route-arrows',
              type: 'symbol',
              source: 'route',
              layout: {
                'symbol-placement': 'line',
                'symbol-spacing': 80,
                'text-field': '▶',
                'text-size': 13,
                'text-keep-upright': false,
                'text-allow-overlap': true,
              },
              paint: { 'text-color': '#5a3ff0', 'text-halo-color': '#ffffff', 'text-halo-width': 1.4 },
            });
          }
        }

        // Numbered markers.
        plotted.forEach((s) => {
          const el = document.createElement('div');
          el.style.cssText = 'background:#7c5cff;color:#fff;border-radius:50%;width:26px;height:26px;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:700;box-shadow:0 1px 5px rgba(0,0,0,.45);border:2px solid #fff;cursor:pointer;';
          el.textContent = String(s.order);
          new maplibregl.Marker({ element: el })
            .setLngLat(s.coord)
            .setPopup(new maplibregl.Popup({ offset: 18 }).setText(`${s.order}. ${s.label}`))
            .addTo(map);
        });

        // Frame all stops.
        const bounds = new maplibregl.LngLatBounds();
        plotted.forEach((s) => bounds.extend(s.coord));
        map.fitBounds(bounds, { padding: 50, maxZoom: 11, duration: 0 });
      };

      if (map.isStyleLoaded()) drawOverlay();
      else map.on('load', drawOverlay);
    });

    return () => {
      cancelled = true;
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [open, stopsKey]); // eslint-disable-line

  // ── Modal mode ────────────────────────────────────────────────────
  if (isModalMode) {
    if (!open) return null;
    return (
      <div
        style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(0,0,0,0.75)', display: 'flex',
          flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        }}
        onClick={(e) => { if (e.target === e.currentTarget && onClose) onClose(); }}
      >
        <div style={{ background: '#1a1a2e', borderRadius: 16, overflow: 'hidden', width: '90vw', maxWidth: 800, maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
            <span style={{ color: '#fff', fontWeight: 600, fontSize: 16 }}>Trip Map</span>
            <button
              onClick={onClose}
              style={{ background: 'none', border: 'none', color: '#aaa', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}
              aria-label="Close map"
            >
              ×
            </button>
          </div>
          {stops.length === 0 ? (
            <div style={{ padding: 32, color: '#aaa', textAlign: 'center' }}>
              No cities found in your itinerary.
            </div>
          ) : (
            <div ref={mapRef} style={{ flex: 1, minHeight: 400 }} />
          )}
        </div>
      </div>
    );
  }

  // ── Inline mode (legacy) ──────────────────────────────────────────
  return (
    <div style={{ width: '100%', height: '350px', marginBottom: 24 }} ref={mapRef} />
  );
};

export default TripMap;
